import React, { useState } from "react";
import { WelcomeOverlay } from "@/components/WelcomeOverlay";
import { OnboardingTour } from "@/components/OnboardingTour/OnboardingTour";
import { LocationDetailsStep } from "@/components/OnboardingTour/LocationDetailsStep";
import { LOCATION_EDITOR_ROLES } from "@/pages/ManageLocations/manageLocationsService";

export interface OnboardingFlowProps {
  userRole: string | null;
  onComplete: () => void | Promise<void>;
}

type OnboardingPhase = "welcome" | "tour" | "location";

const isLocationEditor = (role?: string | null): boolean =>
  LOCATION_EDITOR_ROLES.includes(
    role?.trim().toUpperCase().replace(/[\s-]/g, "_") ?? "",
  );

/**
 * Whether any of the user's accounts lets them update locations. Reads the accounts response
 * cached at login so no extra request is made; null when it isn't available.
 */
const hasLocationEditorAccount = (): boolean | null => {
  try {
    const consumerUsers = JSON.parse(
      localStorage.getItem("oneportal_consumer_data_full") ?? "null",
    )?.data?.consumerUsers;

    return Array.isArray(consumerUsers)
      ? consumerUsers.some((consumerUser) => isLocationEditor(consumerUser?.role))
      : null;
  } catch {
    return null;
  }
};

/**
 * Orchestrates the full first-login onboarding experience:
 * welcome screen -> feature tour -> location confirmation -> onComplete.
 */
export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({
  userRole,
  onComplete,
}) => {
  const [phase, setPhase] = useState<OnboardingPhase>("welcome");
  // The active role only reflects the first account, so any editable account decides first.
  const [canUpdateLocation] = useState(
    () => hasLocationEditorAccount() ?? isLocationEditor(userRole),
  );
  const completeTour = () => {
    if (canUpdateLocation) {
      setPhase("location");
      return;
    }
    onComplete();
  };

  if (phase === "welcome") {
    return <WelcomeOverlay onGetStarted={() => setPhase("tour")} />;
  }

  if (phase === "tour") {
    return (
      <OnboardingTour
        onBack={() => setPhase("welcome")}
        completesOnLastStep={!canUpdateLocation}
        onFinish={completeTour}
        onSkip={completeTour}
      />
    );
  }

  return (
    <LocationDetailsStep
      onBack={() => setPhase("tour")}
      onFinish={onComplete}
    />
  );
};

export default OnboardingFlow;
